import bigJs from 'big.js';

/** Remaining available limit from transfer protection settings */
export type RemainingAvailableLimit = {
  value: string;
  currency?: string;
};

export type TransferSettingsLimitResult = {
  fiatLimit: RemainingAvailableLimit;
  hasTransferSettingsLimit: boolean;
};

/**
 * Calculates effective fiat limit when transfer protection may be active.
 * Returns the minimum of the configured limit and the balance.
 *
 * Cases:
 *   | Condition                    | Result      | hasTransferSettingsLimit |
 *   |------------------------------|-------------|--------------------------|
 *   | No limit configured          | balance     | false                    |
 *   | Currencies don't match       | balance     | false                    |
 *   | limit >= balance             | balance     | false                    |
 *   | limit < balance              | limit       | true                     |
 *
 * When hasTransferSettingsLimit is true, consumers should force fiat input mode
 * and validate against the fiat limit rather than the crypto balance.
 */
export function getTransferSettingsLimitOrAccountBalance(
  remainingAvailableLimit: RemainingAvailableLimit | undefined,
  balance: RemainingAvailableLimit,
): TransferSettingsLimitResult {
  // No limit configured - use balance
  if (!remainingAvailableLimit) {
    return {
      fiatLimit: { value: balance.value, currency: balance.currency },
      hasTransferSettingsLimit: false,
    };
  }

  // Different currencies - can't compare, use balance
  if (remainingAvailableLimit.currency !== balance.currency) {
    return {
      fiatLimit: { value: balance.value, currency: balance.currency },
      hasTransferSettingsLimit: false,
    };
  }

  const limitValue = bigJs(remainingAvailableLimit.value);
  const balanceValue = bigJs(balance.value || '0');

  // Balance is 0 or limit >= balance - use balance
  if (balanceValue.lte(0) || limitValue.gte(balanceValue)) {
    return {
      fiatLimit: { value: balance.value, currency: balance.currency },
      hasTransferSettingsLimit: false,
    };
  }

  // Transfer settings limit is active: limit < balance
  return {
    fiatLimit: {
      value: remainingAvailableLimit.value,
      currency: remainingAvailableLimit.currency,
    },
    hasTransferSettingsLimit: true,
  };
}
